import { computed, ref, watch, type Ref } from 'vue'

interface IActivatable {
  id: string
  deactivated_at: string | null
}

export type SelectionVariant = 'active' | 'inactive'

/**
 * Row selection for tables whose rows can be deactivated/reactivated. The header checkbox cycles
 * through: all active rows -> all inactive rows -> nothing. A mixed selection falls back to the usual
 * select-all/clear toggle.
 */
export function useActivationSelection<T extends IActivatable>(rows: Ref<T[] | undefined>) {
  const selectedIds = ref(new Set<string>())

  const isInactive = (row: T) => row.deactivated_at != null

  const selected = computed(() => (rows.value ?? []).filter((row) => selectedIds.value.has(row.id)))
  const allSelected = computed(() => selected.value.length > 0 && selected.value.length === rows.value?.length)
  const partiallySelected = computed(() => selected.value.length > 0 && !allSelected.value)
  const headerVariant = computed<SelectionVariant>(() =>
    selected.value.length > 0 && selected.value.every(isInactive) ? 'inactive' : 'active'
  )

  const canDeactivate = computed(() => selected.value.length > 0 && selected.value.every((row) => !isInactive(row)))
  const canReactivate = computed(() => selected.value.length > 0 && selected.value.every(isInactive))
  const isMixed = computed(() => selected.value.some(isInactive) && selected.value.some((row) => !isInactive(row)))

  const isSelected = (row: T) => selectedIds.value.has(row.id)

  const setSelection = (selection: T[]) => {
    selectedIds.value = new Set(selection.map((row) => row.id))
  }

  const toggleRow = (row: T, value: boolean) => {
    const next = new Set(selectedIds.value)
    if (value) next.add(row.id)
    else next.delete(row.id)
    selectedIds.value = next
  }

  const clearSelection = () => {
    selectedIds.value = new Set()
  }

  const cycleSelection = () => {
    const all = rows.value ?? []
    const active = all.filter((row) => !isInactive(row))
    const inactive = all.filter(isInactive)
    const selectedRows = selected.value

    if (isMixed.value) {
      if (allSelected.value) clearSelection()
      else setSelection(all)
      return
    }

    const isExactly = (group: T[]) =>
      group.length > 0 && selectedRows.length === group.length && selectedRows.every((r) => group.includes(r))

    if (isExactly(active)) {
      if (inactive.length > 0) setSelection(inactive)
      else clearSelection()
    } else if (isExactly(inactive)) {
      clearSelection()
    } else if (active.length > 0) {
      setSelection(active)
    } else {
      setSelection(inactive)
    }
  }

  // Selection only applies to the rows on screen
  watch(rows, (current) => {
    const ids = new Set((current ?? []).map((row) => row.id))
    selectedIds.value = new Set([...selectedIds.value].filter((id) => ids.has(id)))
  })

  return {
    selected,
    allSelected,
    partiallySelected,
    headerVariant,
    canDeactivate,
    canReactivate,
    isMixed,
    isSelected,
    toggleRow,
    cycleSelection,
    clearSelection,
  }
}
